import { Turn } from "./model/turn";
import {
  DeviceType,
  RuntimeDeviceHaveMicrophone,
  RuntimeDeviceHaveScreenShare,
  RuntimeDeviceHaveWebCam,
  RuntimeDeviceType,
} from "./lib/broswer_utils";
import { Lock } from "./lib/lock";
import { VP8_VideoQualities } from "./video/VP8";
import { VideoQuality } from "./video/videoQuality";

/** @internal */
enum MediaStreamType {
  WEBCAM,
  MICROPHONE,
  SCREENCAPTURE, // TODO: soon ...
}

/** @internal */
export class RahatLineWebRTCPeerConnection {
  private _pc: RTCPeerConnection;

  private readonly _allowToRecvVideo: boolean;
  private readonly _allowToRecvAudio: boolean;

  private readonly _lockGetStream: Lock = new Lock();
  private readonly _rtpSenders: Array<RTCRtpSender> = [];

  private _localWebCamStream: MediaStream | undefined;
  private _currentVideoQuality: VideoQuality | undefined;
  private _localMicrophoneStream: MediaStream | undefined;
  private _localCanvasMediaStream: MediaStream | undefined;
  private _localScreenCaptureStream: MediaStream | undefined;

  public TriggerLocalWebcam: () => void | undefined;
  public OnIceCandidate: ((ice: RTCIceCandidate) => void) | undefined;
  public OnRemoveTrack: ((id: string, type: string) => void) | undefined;
  public OnNewTrack:
    | ((id: string, type: string, stream: MediaStream) => void)
    | undefined;
  public OnConnectionStateChanged:
    | ((connectionState: RTCPeerConnectionState) => void)
    | undefined;

  constructor(
    turnInfo: Turn[],
    allowToRecvAudio: boolean,
    allowToRecvVideo: boolean
  ) {
    this._allowToRecvAudio = allowToRecvAudio;
    this._allowToRecvVideo = allowToRecvVideo;
    this._pc = new RTCPeerConnection({
      iceTransportPolicy: "all",
      bundlePolicy: "max-bundle",
      rtcpMuxPolicy: "require",
      iceServers: turnInfo.map(
        (x) =>
          ({
            urls: x.urls,
            username: x.username,
            credential: x.credential,
          } as RTCIceServer)
      ),
    });

    this._pc.onconnectionstatechange = (e) => {
      if (!!this.OnConnectionStateChanged)
        this.OnConnectionStateChanged(this._pc.connectionState);
    };

    this._pc.ontrack = async (trackEvent) => {
      trackEvent.streams.forEach((stream: MediaStream) => {
        stream.onremovetrack = async ({ track }) => {
          if (!!this.OnRemoveTrack) {
            this.OnRemoveTrack(track.id, track.kind);
          }

          if (track.kind == "video") {
            await this._TriggerVideoReplaceTrack();
          }
        };
      });

      if (!!this.OnNewTrack) {
        const type = trackEvent.track.kind;
        const id = trackEvent.track.id;
        const stream = trackEvent.streams[0];

        this.OnNewTrack(id, type, stream);
      }

      if (trackEvent.track.kind == "video") {
        await this._TriggerVideoReplaceTrack();
      }
    };

    if (RuntimeDeviceType() == DeviceType.IOS) {
      this._pc.createDataChannel("empty");
    }

    // if (RuntimeDeviceType() == DeviceType.OTHER) {
    this._pc.onicecandidate = (ice) => {
      if (!!ice && !!ice.candidate && this.OnIceCandidate)
        this.OnIceCandidate(ice.candidate);
    };
    // }
  }

  public get LocalScreenShareStream(): MediaStream | undefined {
    return this._localScreenCaptureStream;
  }

  public get LocalMicrophoneStream(): MediaStream | undefined {
    return this._localMicrophoneStream;
  }

  public get LocalWebcamStream(): MediaStream | undefined {
    return this._localWebCamStream;
  }

  public get LocalCanvasStream(): MediaStream | undefined {
    return this._localCanvasMediaStream;
  }

  public Close(): void {
    if (this._localWebCamStream)
      this._localWebCamStream.getTracks().forEach((t) => t.stop());
    if (this._localMicrophoneStream)
      this._localMicrophoneStream.getTracks().forEach((t) => t.stop());
    if (this._localCanvasMediaStream)
      this._localCanvasMediaStream.getTracks().forEach((t) => t.stop());
    this._pc.close();
  }

  /**
   * to complete local negotiation (client send an offer and wait for server answer), must add server answer
   * @param serverAnswer
   * @constructor
   */
  public async CompleteLocalNegotiation(
    serverAnswer: RTCSessionDescription
  ): Promise<void> {
    await this._pc.setRemoteDescription(serverAnswer);
  }

  /**
   * create a negotiation with server
   * @param serverOffer
   * if server offer have value, means you want to renegotiate to server and send local answer to server,
   * else means you start a new negotiation with server
   * @constructor
   */
  public Negotiate(
    serverOffer: RTCSessionDescription | undefined = undefined
  ): Promise<RTCSessionDescription> {
    return serverOffer == undefined
      ? this._CreateOffer()
      : this._CreateAnswer(serverOffer);
  }

  public async StartAudioInputStream(): Promise<void> {
    if (this._localMicrophoneStream !== undefined) {
      throw new Error("Microphone stream already started");
    }

    this._localMicrophoneStream = await this._GetMediaStream(
      MediaStreamType.MICROPHONE
    );
    await this._AddStreamTrack(this._localMicrophoneStream);
  }

  public async StopAudioInputStream(): Promise<void> {
    await this._lockGetStream.acquire();
    try {
      if (this._localMicrophoneStream === undefined) {
        throw new Error("Microphone stream not started");
      }

      this._localMicrophoneStream.getTracks().forEach((track) => track.stop());
      this._RemoveStreamTrack(this._localMicrophoneStream);
      this._localMicrophoneStream = undefined;
    } finally {
      this._lockGetStream.release();
    }
  }

  public async StartWebCamStream(): Promise<void> {
    if (this._localWebCamStream !== undefined) {
      throw new Error("Webcam stream already started");
    }

    this._localWebCamStream = await this._GetMediaStream(
      MediaStreamType.WEBCAM
    );
    (this.TriggerLocalWebcam ?? (() => {}))();
    await this._AddStreamTrack(this._localWebCamStream);
  }

  public async StopWebCamStream(): Promise<void> {
    await this._lockGetStream.acquire();
    try {
      if (this._localWebCamStream === undefined) {
        throw new Error("Webcam stream not started");
      }

      this._StopLocalStream(this._localWebCamStream);
      this._RemoveStreamTrack(this._localWebCamStream);
      this._localWebCamStream = undefined;
    } finally {
      this._lockGetStream.release();
    }
  }

  public async StartScreenShareStream(): Promise<void> {
    if (this._localScreenCaptureStream !== undefined) {
      throw new Error("screen share already started");
    }

    this._localScreenCaptureStream = await this._GetMediaStream(
      MediaStreamType.SCREENCAPTURE
    );
    await this._AddStreamTrack(this._localScreenCaptureStream);
  }

  public async StopScreenShareStream(): Promise<void> {
    await this._lockGetStream.acquire();
    try {
      if (this._localScreenCaptureStream === undefined) {
        throw new Error("Screen share stream not started");
      }

      this._StopLocalStream(this._localScreenCaptureStream);
      this._RemoveStreamTrack(this._localScreenCaptureStream);
      this._localScreenCaptureStream = undefined;
    } finally {
      this._lockGetStream.release();
    }
  }

  public async StartStreamCanvas(
    canvas: HTMLCanvasElement,
    frameRate: number
  ): Promise<void> {
    await this._lockGetStream.acquire();
    try {
      if (!!this._localCanvasMediaStream)
        throw new Error("Canvas already on stream");

      this._localCanvasMediaStream = canvas.captureStream(frameRate);
      await this._AddStreamTrack(this._localCanvasMediaStream);
    } finally {
      this._lockGetStream.release();
    }
  }

  public async StopStreamCanvas(): Promise<void> {
    await this._lockGetStream.acquire();
    try {
      if (this._localCanvasMediaStream === undefined) {
        throw new Error("Canvas stream not started");
      }

      this._StopLocalStream(this._localCanvasMediaStream);
      this._RemoveStreamTrack(this._localCanvasMediaStream);
      this._localCanvasMediaStream = undefined;
    } finally {
      this._lockGetStream.release();
    }
  }

  private _GetCurrentVideoQuality(): VideoQuality {
    const inboundVideos = this._pc
      .getReceivers()
      .filter((x) => x.track.id.includes("_"))
      .filter((x) => x.track?.kind == "video").length;

    const outboundVideos = this._pc
      .getSenders()
      .filter((x) => x.track?.id.includes("_"))
      .filter((x) => x.track?.kind == "video").length;

    const totalVideos = outboundVideos + inboundVideos;
    const matchItem = VP8_VideoQualities.filter((x) => x.MaximumConnections > 0)
      .sort((a, b) => a.MaximumConnections - b.MaximumConnections)
      .find((x) => x.MaximumConnections > totalVideos);

    return !!matchItem
      ? matchItem
      : VP8_VideoQualities.filter(() => true).sort(
          (a, b) => a.MaximumConnections - b.MaximumConnections
        )[VP8_VideoQualities.length - 1];
  }

  private async _AddStreamTrack(stream: MediaStream) {
    // const rtpSender = this._pc.addTrack(stream.getTracks()[0], stream);
    // this._rtpSenders.push(rtpSender);

    for (const track of stream.getTracks()) {
      const rtp = this._pc.addTransceiver(track, {
        direction: "sendrecv",
      });

      if (track.kind == "video") {
        const codecs = RTCRtpSender.getCapabilities("video")!.codecs;
        const vp8Codecs = codecs.filter(
          (codec) => codec.mimeType === "video/VP8"
        );
        const parameters = rtp.sender.getParameters();
        if (!parameters.encodings) {
          parameters.encodings = [{}];
        }
        parameters.encodings[0].maxBitrate = 5000000;

        await rtp.sender.setParameters(parameters);
        rtp.setCodecPreferences(vp8Codecs);
      }

      this._rtpSenders.push(rtp.sender);
    }
  }

  private _StopLocalStream(stream: MediaStream) {
    const trackIds = stream.getTracks().map((x) => x.id);
    const tracks = stream.getTracks().filter((x) => trackIds.includes(x.id));

    for (const track of tracks) {
      track.stop();
    }
  }

  private _RemoveStreamTrack(stream: MediaStream): void {
    const index = this._rtpSenders.findIndex(
      (x) => stream.getTracks().findIndex((y) => y.id == x.track?.id) >= 0
    );
    if (index < 0) {
      throw new Error(`Unable to remove track, index out of range`);
    }

    this._pc.removeTrack(this._rtpSenders[index]);
    this._rtpSenders.splice(index, 1);
  }

  private async _TriggerVideoReplaceTrack(): Promise<void> {
    if (!this._localWebCamStream) return;

    const newQuality = this._GetCurrentVideoQuality();
    if (this._RequireToChangeQuality(newQuality)) {
      for (const track of this._localWebCamStream.getTracks()) track.stop();

      this._localWebCamStream = await this._GetMediaStream(
        MediaStreamType.WEBCAM
      );
      (this.TriggerLocalWebcam ?? (() => {}))();
      await this._rtpSenders
        .find((x) => x.track.kind == "video")
        .replaceTrack(this._localWebCamStream.getTracks()[0]);
    }
  }

  private async _CreateOffer(): Promise<RTCSessionDescription> {
    const offer = await this._pc.createOffer({
      offerToReceiveAudio: this._allowToRecvAudio,
      offerToReceiveVideo: this._allowToRecvVideo,
    });

    await this._pc.setLocalDescription(offer);

    // if (RuntimeDeviceType() == DeviceType.IOS) {
    //     // wait until ice connection
    //     const pcs = new PromiseCompletionSource();
    //     if (this._pc.iceGatheringState == "complete") pcs.Resolve();
    //     else {
    //         this._pc.addEventListener("icegatheringstatechange", () => {
    //             if (this._pc.iceGatheringState == "complete") {
    //                 // this._pc.removeEventListener("icegatheringstatechange", null);
    //                 pcs.Resolve();
    //             }
    //         });
    //     }

    //     await pcs.Promise;
    // }

    if (!this._pc.localDescription)
      throw new Error("localDescription is empty");
    return this._pc.localDescription;
  }

  private _RequireToChangeQuality(quality: VideoQuality): boolean {
    return (
      !this._currentVideoQuality || !this._currentVideoQuality.Compare(quality)
    );
  }

  private async _GetMediaStream(type: MediaStreamType): Promise<MediaStream> {
    await this._lockGetStream.acquire();

    try {
      switch (type) {
        case MediaStreamType.MICROPHONE:
          if (!(await RuntimeDeviceHaveMicrophone()))
            throw new Error("audio input is not supported");

          return navigator.mediaDevices.getUserMedia({
            audio: {
              sampleRate: { ideal: 48000 },
              echoCancellation: { ideal: true },
              noiseSuppression: { ideal: true },
              autoGainControl: { ideal: true },
            },
          });

        case MediaStreamType.SCREENCAPTURE:
          if (!(await RuntimeDeviceHaveScreenShare()))
            throw new Error("screenshare is not supported");

          return navigator.mediaDevices.getDisplayMedia({
            video: {
              frameRate: { ideal: 30 },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              sampleRate: { ideal: 24000 },
            },
            audio: false,
          });

        case MediaStreamType.WEBCAM:
          if (!(await RuntimeDeviceHaveWebCam()))
            throw new Error("webcam is not supported");

          this._currentVideoQuality = this._GetCurrentVideoQuality();
          return navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: this._currentVideoQuality.Width },
              height: { ideal: this._currentVideoQuality.Height },
              frameRate: { ideal: this._currentVideoQuality.FrameRate },
              facingMode: "user",
            },
          });

        default:
          throw new Error(`Not implemented Type ${type}`);
      }
    } finally {
      this._lockGetStream.release();
    }
  }

  private async _CreateAnswer(
    serverOffer: RTCSessionDescription
  ): Promise<RTCSessionDescription> {
    await this._pc.setRemoteDescription(serverOffer);

    const local = await this._pc.createAnswer();
    await this._pc.setLocalDescription(local);

    if (!this._pc.localDescription)
      throw new Error("localDescription is empty");
    return this._pc.localDescription;
  }
}
